import { ActionTree } from 'vuex'
import { AppHub } from '@/event-hub'
import { cachedRequest, clearCache } from './caching'
import { http } from './http'
import State from './state'
import { PullRequestUpdateStatus } from '@/interfaces'

export const actions: ActionTree<typeof State, typeof State> = {
  refresh({ commit, dispatch }) {
    commit('resetData')
    dispatch('fetchAccount', true)
    AppHub.$emit('refresh')
    return Promise.resolve()
  },
  fetchParts({ state, commit }, refresh = false) {
    return cachedRequest({
      url: '/api/Inventory',
      params: {
        accountId: state.accountId,
      },
      isLoading: () => {
        commit('setParts', { data: [], isLoading: true })
      },
      resolve: (response: any) => {
        commit('setParts', { data: response.data })
      },
      reject: () => {
        commit('setParts', { data: [], hasError: true })
      },
      refresh,
    })
  },

  fetchAccount({ state, commit }, refresh = false) {
    return cachedRequest({
      url: '/api/account',
      params: {
        accountId: state.accountId,
      },
      resolve: (response: any) => {
        commit('setAccount', response.data)
      },
      refresh,
    })
  },

  setAccountId({ state, commit, dispatch }, accountId) {
    const lastAccountId = state.accountId
    commit('setAccountId', accountId)

    // If the account changes, refresh the UI
    if (lastAccountId !== accountId) {
      return dispatch('refresh')
    }

    return Promise.resolve()
  },

  fetchAccountList({ commit, state }, refresh = false) {
    return cachedRequest({
      url: '/api/account/list',
      resolve: (response: any) => {
        commit('setAccountList', response.data)
      },
      refresh,
    })
  },

  fetchMessages({ state, commit }, refresh = false) {
    return cachedRequest({
      url: '/api/message',
      params: {
        accountId: state.accountId,
      },
      resolve: (response: any) => {
        commit('setMessages', response.data)
      },
      refresh,
    })
  },

  putMessagesRead({ state, commit, dispatch }, ids) {
    return new Promise((resolve, reject) => {
      http
        .put(
          '/api/message',
          {
            action: 'Read',
            messageIds: ids,
          },
          {
            params: { accountId: state.accountId },
          },
        )
        .then(() => {
          commit('setMessagesRead', ids)
          resolve()
        })
        .catch(error => {
          reject(error)
          console.log(error)
        })
    })
  },

  putMessagesDelete({ state, commit, dispatch }, ids) {
    return new Promise((resolve, reject) => {
      http
        .put(
          '/api/message',
          {
            action: 'Delete',
            messageIds: ids,
          },
          {
            params: { accountId: state.accountId },
          },
        )
        .then(() => {
          commit('setMessagesDelete', ids)
          resolve()
        })
        .catch(error => {
          reject(error)
          console.log(error)
        })
    })
  },

  putMessagesClearAlerts({ state, commit, dispatch }, ids) {
    return new Promise((resolve, reject) => {
      http
        .put(
          '/api/message',
          {
            action: 'ClearAlert',
            messageIds: ids,
          },
          {
            params: { accountId: state.accountId },
          },
        )
        .then(() => {
          commit('setMessagesClearAlerts', ids)
          resolve()
        })
        .catch(error => {
          reject(error)
          console.log(error)
        })
    })
  },

  fetchEmailList({ commit, state }, refresh = false) {
    //Api errors if csos try and get cso list and vice versa, so only grab one or the other
    const emailUrl = state.isCSO ? '/api/account/customers' : '/api/account/csos'
    return cachedRequest({
      url: emailUrl,
      params: {
        accountId: state.accountId,
      },
      resolve: (response: any) => {
        commit('setEmailList', response.data)
      },
      refresh,
    })
  },

  fetchPartners({ commit, state }, { salesOrg, soldToId, refresh }) {
    //Api errors if csos try and get cso list and vice versa, so only grab one or the other
    return cachedRequest({
      url: '/api/account/partners',
      params: {
        accountId: state.accountId,
        soldToId,
        salesOrg: salesOrg.id,
        distributionChannel: salesOrg.distributionChannel,
        division: salesOrg.division,
      },
      refresh,
    })
  },

  fetchPurchaseOrders({ commit, state }, { refresh, ...data }) {
    return cachedRequest({
      url: '/api/PullRequest/purchase-orders',
      method: 'post',
      params: {
        accountId: state.accountId,
      },
      data,
      isLoading: () => {
        commit('setPurchaseOrders', { data: [], isLoading: true })
      },
      resolve: (response: any) => {
        commit('setPurchaseOrders', { data: response.data })
      },
      reject: () => {
        commit('setPurchaseOrders', { data: [], hasError: true })
      },
      refresh,
    })
  },

  fetchReplenishments({ commit, state }, refresh = false) {
    return cachedRequest({
      url: '/api/replenishment',
      params: {
        accountId: state.accountId,
      },
      isLoading: () => {
        commit('setReplenishments', { data: {}, isLoading: true })
      },
      resolve: (response: any) => {
        commit('setReplenishments', { data: response.data })
      },
      reject: () => {
        commit('setReplenishments', { data: {}, hasError: true })
      },
      refresh,
    })
  },

  fetchPullRequestById({ getters, dispatch }, id: string, refresh = false) {
    let pull = getters.getPullRequestById(id)
    if (pull) return Promise.resolve(pull)
    return dispatch('fetchPullRequests', true).then(() => {
      pull = getters.getPullRequestById(id)
      return Promise.resolve(pull)
    })
  },

  fetchPullRequests({ commit, state }, refresh = false) {
    return cachedRequest({
      url: '/api/pullrequest',
      params: {
        accountId: state.accountId,
      },
      isLoading: () => {
        commit('setPullRequests', { data: [], isLoading: true })
      },
      resolve: (response: any) => {
        commit('setPullRequests', { data: response.data })
      },
      reject: () => {
        commit('setPullRequests', { data: [], hasError: true })
      },
      refresh,
    })
  },

  updatePullRequestStatus({ state }, status: PullRequestUpdateStatus) {
    return http.patch('/api/pullrequest/status', status, {
      params: {
        accountId: state.accountId,
      },
    })
  },

  fetchShipments({ state, commit }, refresh = false) {
    return cachedRequest({
      url: '/api/Shipment',
      params: {
        accountId: state.accountId,
      },
      isLoading: () => {
        commit('setShipment', { data: [], isLoading: true })
      },
      resolve: (response: any) => {
        commit('setShipment', { data: response.data })
      },
      reject: () => {
        commit('setShipment', { data: [], hasError: true })
      },
      refresh,
    })
  },

  fetchShipmentById({ getters, dispatch }, id: string) {
    let shipment = getters.getShipmentById(id)
    if (shipment) return Promise.resolve(shipment)
    return dispatch('fetchShipments', true).then(() => {
      shipment = getters.getShipmentById(id)
      return Promise.resolve(shipment)
    })
  },

  setCSOFlag({ commit }) {
    commit('setCSOFlag')
    return Promise.resolve()
  },

  setUserName({ commit }) {
    commit('setUserName')
    return Promise.resolve()
  },
  setSelectedPart({ commit }) {
    commit('setSelectedPart', '')
    return Promise.resolve()
  },

  createPullRequest({ state, dispatch }, data) {
    const formData = new FormData()
    Object.keys(data.pullRequest).forEach(key => {
      formData.append(key, data.pullRequest[key])
    })
    if (data.isNewPR) {
      return http
        .post('/api/PullRequest', formData, {
          params: { accountId: state.accountId },
        })
        .then(response => {
          dispatch('fetchPullRequests', true)
          dispatch('fetchMessages', true)
          return Promise.resolve(response)
        })
    } else {
      return http
        .patch('/api/PullRequest', formData, {
          params: { accountId: state.accountId },
        })
        .then(response => {
          // Note: fetchPullRequests not necessary when updating - it's called when updating pull status
          dispatch('fetchMessages', true)
          return Promise.resolve(response)
        })
    }
  },

  sendEmail({ state, commit, dispatch }, message) {
    return new Promise((resolve, reject) => {
      http
        .post('/api/message/send-email', message, { params: { accountId: state.accountId } })
        .then(() => {
          resolve()
        })
        .catch(error => {
          console.log(error)
          reject(error)
        })
    })
  },
}
